/* One appearance service; sections only render controls for its choices. */
(() => {
  'use strict';
  if (window.ThemeAppearance || !window.appearanceBoot) return;
  const { config, state: engine, media } = window.appearanceBoot;
  const root = document.documentElement;
  const controls = new Set();
  function render(state, source) {
    AppearanceState.paint(root, state);
    controls.forEach(control => control.sync());
    document.dispatchEvent(new CustomEvent('theme:color-mode', { detail: { mode: state.id, ...state, source } }));
    return state;
  }
  const api = window.ThemeAppearance = {
    get current() { return engine.snapshot(); },
    get palettes() { return engine.palettes; },
    get allowSwitching() { return config.allowSwitching; },
    set(id) {
      if (!config.allowSwitching || !engine.valid(id)) return engine.snapshot();
      return render(engine.select(id, true), 'visitor');
    },
    reset() { return api.set(config.defaultChoice); }
  };
  class AppearanceControl extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.buttons = [...this.querySelectorAll('button')];
      if (!this.buttons.length) return;
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        api.set(button.dataset.appearanceChoice || (api.current.id === 'dark' ? 'light' : 'dark'));
      }, { signal });
      if (this.matches('color-tabs')) this.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        if (!this.buttons.includes(document.activeElement)) return;
        event.preventDefault();
        const index = this.buttons.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.buttons.length - 1
          : (index + (['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1) + this.buttons.length) % this.buttons.length;
        const button = this.buttons[next];
        button.focus();
        api.set(button.dataset.appearanceChoice);
      }, { signal });
      controls.add(this);
      this.sync();
      this.hidden = !config.allowSwitching;
      this.buttons.forEach(button => { button.hidden = false; });
    }
    sync() {
      const current = api.current;
      const selected = this.buttons.findIndex(button => button.dataset.appearanceChoice === current.id);
      this.style.setProperty('--selected-index', Math.max(0, selected));
      this.dataset.count = this.buttons.length;
      this.buttons.forEach((button, index) => {
        if (!button.dataset.appearanceChoice) {
          button.setAttribute('aria-pressed', String(current.id === 'dark'));
          button.title = `Switch to ${current.id === 'dark' ? 'light' : 'dark'}`;
          button.querySelectorAll('[data-appearance-word]').forEach(word => {
            word.style.visibility = word.dataset.appearanceWord === current.id ? 'visible' : 'hidden';
          });
          return;
        }
        const checked = button.dataset.appearanceChoice === current.id;
        button.setAttribute('aria-checked', String(checked));
        button.tabIndex = checked ? 0 : -1;
        // Active card comes forward; logical DOM and keyboard order stay stable.
        const slot = (index - selected + this.buttons.length) % this.buttons.length;
        button.dataset.slot = slot;
        button.style.setProperty('--slot', slot);
      });
      this.querySelector('[data-appearance-preview]')?.setAttribute('data-palette', current.id);
    }
    disconnectedCallback() { this.abort?.abort(); controls.delete(this); }
  }
  customElements.define('color-mode-toggle', class extends AppearanceControl {});
  customElements.define('color-tabs', class extends AppearanceControl {});
  window.addEventListener('storage', event => {
    if (config.editor || !config.allowSwitching) return;
    if (event.key === engine.key || event.key === null) render(engine.select(event.newValue), 'storage');
  });
  window.addEventListener('pageshow', () => render(engine.restore(), 'history'));
  media.addEventListener('change', () => {
    if (engine.snapshot().choice === 'system') render(engine.snapshot(), 'device');
  });
  render(engine.snapshot(), 'ready');
})();
